import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Autocomplete,
  TableHead,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api';
import dayjs from 'dayjs';

export default function ProjectMemberList({ projectId, workSums = {}, setWorkSums }) {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [workInputs, setWorkInputs] = useState({});

  // xlsx 行16: 工数の入力単位を「分」「人日」で切替。1人日=N時間は backend から取得
  const [workUnit, setWorkUnit] = useState('minutes'); // 'minutes' | 'days'
  const [hoursPerPersonDay, setHoursPerPersonDay] = useState(8);
  useEffect(() => {
    api
      .get('/api/settings/person-day-hours')
      .then((r) => setHoursPerPersonDay(Number(r.data?.hoursPerPersonDay) || 8))
      .catch(() => { /* デフォルト8h でフォールバック */ });
  }, []);
  const minutesPerDay = hoursPerPersonDay * 60;
  const fmtSum = (m) => {
    if (m == null) return '0';
    if (workUnit === 'days') return `${(m / minutesPerDay).toFixed(2)} 人日`;
    return `${m} 分`;
  };

  const [roleManagerOpen, setRoleManagerOpen] = useState(false);
  const [roleEdit, setRoleEdit] = useState(null);
  const [roleName, setRoleName] = useState('');

  const [localWorkSums, setLocalWorkSums] = useState({});
  const effectiveWorkSums = useMemo(
    () => (typeof setWorkSums === 'function' ? workSums : localWorkSums),
    [setWorkSums, workSums, localWorkSums]
  );
  const setEffectiveWorkSums =
    typeof setWorkSums === 'function' ? setWorkSums : setLocalWorkSums;

  const fetchMembersAndUsers = useCallback(async () => {
    const [membersRes, usersRes, rolesRes] = await Promise.all([
      api.get('/api/projectMembers', { params: { projectId } }),
      api.get('/api/users'),
      api.get('/api/roles'),
    ]);

    setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
    setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.users || []);
    setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
  }, [projectId]);

  const fetchWorkloads = useCallback(
    async (membersList) => {
      if (!Array.isArray(membersList) || membersList.length === 0) {
        setEffectiveWorkSums({});
        return;
      }

      const getMonthRange = (offset = 0) => {
        const base = dayjs().add(offset, 'month');
        return {
          from: base.startOf('month').format('YYYY-MM-DD'),
          to: base.endOf('month').format('YYYY-MM-DD'),
        };
      };

      const results = {};
      for (const m of membersList) {
        const thisMonth = getMonthRange(0);
        const lastMonth = getMonthRange(-1);

        const [resThis, resLast] = await Promise.all([
          api.get('/api/workloads', {
            params: {
              project_id: projectId,
              user_id: m.userId,
              from_date: thisMonth.from,
              to_date: thisMonth.to,
            },
          }),
          api.get('/api/workloads', {
            params: {
              project_id: projectId,
              user_id: m.userId,
              from_date: lastMonth.from,
              to_date: lastMonth.to,
            },
          }),
        ]);

        const arrThis = Array.isArray(resThis.data) ? resThis.data : [];
        const arrLast = Array.isArray(resLast.data) ? resLast.data : [];

        const sumThis = arrThis.reduce((s, w) => s + Number(w.hours || 0) * 60, 0);
        const sumLast = arrLast.reduce((s, w) => s + Number(w.hours || 0) * 60, 0);

        results[m.userId] = { this: Math.round(sumThis), last: Math.round(sumLast) };
      }
      setEffectiveWorkSums(results);
    },
    [projectId, setEffectiveWorkSums]
  );

  useEffect(() => {
    fetchMembersAndUsers();
  }, [fetchMembersAndUsers]);

  useEffect(() => {
    fetchWorkloads(members);
  }, [members, fetchWorkloads]);

  const addMember = async () => {
    if (!selectedUser) return;

    if (members.some(m => String(m.userId) === String(selectedUser.id))) {
      alert('このユーザーはすでに登録されています。');
      return;
    }

    await api.post('/api/projectMembers', {
      projectId,
      userId: selectedUser.id,
      roleId: selectedRole?.id || null,
    });

    setSelectedUser(null);
    setSelectedRole(null);
    fetchMembersAndUsers();
  };

  const updateRole = async (memberId, roleId) => {
    await api.put(`/api/projectMembers/${memberId}`, { roleId });
    fetchMembersAndUsers();
  };

  const deleteMember = async (id) => {
    await api.delete(`/api/projectMembers/${id}`);
    setDeleteTarget(null);
    fetchMembersAndUsers();
  };

  const submitWorkload = async (userId) => {
    const raw = Number(workInputs[userId]);
    if (!raw || raw <= 0) return;
    // xlsx 行16: 単位が人日なら 1人日=hoursPerPersonDay 時間で分に換算
    const minutes = workUnit === 'days' ? Math.round(raw * minutesPerDay) : raw;

    try {
      await api.post('/api/workloads', {
        project_id: projectId,
        user_id: userId,
        work_date: new Date().toISOString().slice(0, 10),
        hours: (minutes / 60).toFixed(2),
        memo: '',
      });
      setWorkInputs(v => ({ ...v, [userId]: '' }));
      fetchWorkloads(members);
    } catch (e) {
      alert(`工数登録に失敗しました: ${e.response?.data?.error || e.message}`);
    }
  };

  const saveRole = async () => {
    if (!roleName) return;

    if (roleEdit) {
      await api.put(`/api/roles/${roleEdit.id}`, { name: roleName });
    } else {
      await api.post('/api/roles', { name: roleName });
    }

    setRoleEdit(null);
    setRoleName('');
    fetchMembersAndUsers();
  };

  const deleteRole = async (id) => {
    await api.delete(`/api/roles/${id}`);
    fetchMembersAndUsers();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Autocomplete
          options={users}
          getOptionLabel={o => o?.name || ''}
          value={selectedUser}
          onChange={(e, v) => setSelectedUser(v)}
          sx={{ width: 200 }}
          renderInput={p => <TextField {...p} label="ユーザー" />}
        />
        <Autocomplete
          options={roles}
          getOptionLabel={o => o?.name || ''}
          value={selectedRole}
          onChange={(e, v) => setSelectedRole(v)}
          sx={{ width: 200 }}
          renderInput={p => <TextField {...p} label="役割" />}
        />
        <Button variant="contained" onClick={addMember} disabled={!selectedUser}>
          追加
        </Button>
        <Button variant="outlined" onClick={() => setRoleManagerOpen(true)}>
          役割管理
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">入力単位</Typography>
          <ToggleButtonGroup
            value={workUnit}
            exclusive
            size="small"
            onChange={(_, v) => v && setWorkUnit(v)}
          >
            <ToggleButton value="minutes">分</ToggleButton>
            <ToggleButton value="days">人日</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            (1人日 = {hoursPerPersonDay}時間)
          </Typography>
        </Box>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>メンバー名</TableCell>
            <TableCell>役割</TableCell>
            <TableCell>工数登録</TableCell>
            <TableCell>先月</TableCell>
            <TableCell>今月</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map(m => (
            <TableRow key={m.id}>
              <TableCell>{m.userName}</TableCell>
              <TableCell>
                <TextField
                  select
                  size="small"
                  value={m.roleId || ''}
                  onChange={e => updateRole(m.id, e.target.value)}
                >
                  <MenuItem value="">未設定</MenuItem>
                  {roles.map(r => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={workInputs[m.userId] || ''}
                  onChange={e =>
                    setWorkInputs(v => ({ ...v, [m.userId]: e.target.value }))
                  }
                  inputProps={{
                    min: 0,
                    step: workUnit === 'days' ? 0.25 : 1,
                    style: { width: 80 },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {workUnit === 'days' ? '人日' : '分'}
                      </InputAdornment>
                    ),
                  }}
                />
                <Button size="small" onClick={() => submitWorkload(m.userId)}>
                  登録
                </Button>
              </TableCell>
              <TableCell>{fmtSum(effectiveWorkSums?.[m.userId]?.last)}</TableCell>
              <TableCell>{fmtSum(effectiveWorkSums?.[m.userId]?.this)}</TableCell>
              <TableCell>
                <IconButton onClick={() => setDeleteTarget(m)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* 削除確認 */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>メンバー削除</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>キャンセル</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMember(deleteTarget.id)}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 役割管理 */}
      <Dialog open={roleManagerOpen} onClose={() => setRoleManagerOpen(false)}>
        <DialogTitle>役割管理</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="役割名"
            value={roleName}
            onChange={e => setRoleName(e.target.value)}
          />
          <Button sx={{ mt: 1 }} onClick={saveRole} variant="contained">
            {roleEdit ? '更新' : '追加'}
          </Button>

          {roles.map(r => (
            <Box key={r.id} sx={{ display: 'flex', mt: 1, gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>{r.name}</Box>
              <Button size="small" onClick={() => {
                setRoleEdit(r);
                setRoleName(r.name);
              }}>
                編集
              </Button>
              <Button size="small" color="error" onClick={() => deleteRole(r.id)}>
                削除
              </Button>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
